/**
 * Vendor price-list PDF download.
 *
 * Thin client around the `vendor-price-list-pdf` Edge Function. The
 * function returns a fully-branded PDF (PeptideClients header on every
 * page, vendor info block + QR code on page 1, one clickable button
 * per product line linking to the live product page on
 * https://shop.peptideclients.com/@<slug>/<product-slug>).
 *
 * Auth model
 * ----------
 * verify_jwt = false at the gateway; the function forwards the caller's
 * Authorization header (when present) into a user-scoped supabase-js
 * client so the underlying SECURITY DEFINER RPC
 * (`app_public.get_vendor_price_list`) can decide whether to disclose
 * the catalog:
 *
 *   - Vendor team members (shop.vendor_users row with accepted_at IS
 *     NOT NULL): always allowed, regardless of vendor status.
 *   - Platform admins (core.platform_admins): always allowed -- covers
 *     suspended / draft vendors when admins audit-print.
 *   - Everyone else: allowed iff the vendor opted into a public
 *     storefront (shop.vendors.public_storefront_enabled = true) and
 *     is in a non-rejected state.
 *
 * Surface
 * -------
 * Used by:
 *   - apps/shop  -> public storefront "Download price list (PDF)" CTA
 *                   (forwards the visitor's anon JWT or no JWT at all)
 *   - apps/sell  -> vendor share card "Download price list (PDF)" CTA
 *                   (forwards the vendor's session JWT so the function
 *                    treats them as a team member)
 *   - apps/admin -> moderation surface (forwards the admin's session
 *                    JWT so the function treats them as a platform admin)
 *
 * Two helpers are exported:
 *   - `getVendorPriceListPdfUrl(slug)`: builds the absolute URL.
 *     Useful when the consumer wants to open the PDF in a new tab
 *     (anchor target=_blank). Note: anonymous downloads work, but
 *     authenticated downloads require fetching the bytes via
 *     `downloadVendorPriceListPdf` because the browser cannot attach
 *     the session JWT to a plain anchor click.
 *   - `downloadVendorPriceListPdf(slug)`: performs a fetch with the
 *     caller's access token + the anon key apikey header so the
 *     gateway routes correctly, returns the raw blob. The caller
 *     turns it into an object URL + triggers a download.
 */

import { supabase, supabaseEndpoint } from '../client';

export interface DownloadVendorPriceListPdfResult {
  blob: Blob;
  filename: string;
}

export function getVendorPriceListPdfUrl(slug: string): string {
  const { url } = supabaseEndpoint();
  const safe = encodeURIComponent(slug);
  return `${url}/functions/v1/vendor-price-list-pdf?slug=${safe}`;
}

/**
 * Fetches the PDF bytes for `slug` and returns them as a Blob + the
 * server-suggested filename. The function attaches the current
 * session's access token when one exists so vendor team members and
 * platform admins get the unrestricted "any-vendor-state" path; for
 * anonymous calls (off-platform visitors hitting the public
 * storefront's CTA) it still sends the anon apikey, which is the
 * minimum the Supabase Edge Function gateway requires.
 */
export async function downloadVendorPriceListPdf(
  slug: string,
): Promise<DownloadVendorPriceListPdfResult> {
  const { url, anonKey } = supabaseEndpoint();
  const safe = encodeURIComponent(slug);

  const headers: Record<string, string> = {
    apikey: anonKey,
    accept: 'application/pdf',
  };
  try {
    const { data: sess } = await supabase().auth.getSession();
    const token = sess.session?.access_token;
    headers.authorization = `Bearer ${token ?? anonKey}`;
  } catch {
    headers.authorization = `Bearer ${anonKey}`;
  }

  const resp = await fetch(`${url}/functions/v1/vendor-price-list-pdf?slug=${safe}`, {
    method: 'GET',
    headers,
  });

  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = (await resp.json()) as { error?: string };
      if (body.error) detail = body.error;
    } catch {
      // body was not JSON; keep the HTTP status as the message.
    }
    throw new Error(`Price-list download failed: ${detail}`);
  }

  const blob = await resp.blob();

  // Prefer the server-suggested filename from Content-Disposition; fall
  // back to a slug-derived name. The Content-Disposition header from
  // the edge fn looks like: `inline; filename="<slug>-price-list.pdf"`.
  let filename = `${slug}-price-list.pdf`;
  const dispo = resp.headers.get('content-disposition') ?? '';
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(dispo);
  if (m && m[1]) filename = m[1];

  return { blob, filename };
}

/**
 * Anon-keyed variant for apps that intentionally do NOT call
 * `initSupabase()` -- specifically the public storefront app
 * (`apps/shop/src/PublicStorefrontApp.tsx`), which boots without
 * session plumbing because off-platform visitors have no JWT to
 * thread through. Mirrors the `getPublicVendorPaymentMethodsViaFetch`
 * pattern.
 *
 * No Authorization header is attached beyond the anon key, so the
 * edge function's RPC takes the "public storefront opt-in" branch.
 * Vendor team members and platform admins who want the unrestricted
 * path should use `downloadVendorPriceListPdf` from an app that DOES
 * call `initSupabase` (apps/sell, apps/admin, apps/web).
 */
export async function downloadVendorPriceListPdfViaFetch(
  supabaseUrl: string,
  anonKey: string,
  slug: string,
): Promise<DownloadVendorPriceListPdfResult> {
  const safe = encodeURIComponent(slug);
  const resp = await fetch(
    `${supabaseUrl}/functions/v1/vendor-price-list-pdf?slug=${safe}`,
    {
      method: 'GET',
      headers: {
        apikey: anonKey,
        authorization: `Bearer ${anonKey}`,
        accept: 'application/pdf',
      },
    },
  );
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = (await resp.json()) as { error?: string };
      if (body.error) detail = body.error;
    } catch {
      // body was not JSON; keep the HTTP status as the message.
    }
    throw new Error(`Price-list download failed: ${detail}`);
  }
  const blob = await resp.blob();
  let filename = `${slug}-price-list.pdf`;
  const dispo = resp.headers.get('content-disposition') ?? '';
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(dispo);
  if (m && m[1]) filename = m[1];
  return { blob, filename };
}

/**
 * DOM helper -- pushes a Blob through an `<a download>` click so the
 * browser saves the file with the suggested name. Defers blob-URL
 * revocation to keep Safari happy.
 */
export function saveBlobAsFile(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
  }
}

/**
 * Convenience helper: downloads the PDF and triggers a browser
 * download (in a tab/iframe-friendly way). Returns the filename so
 * the caller can show a "Downloaded foo.pdf" toast.
 *
 * Implementation note: we go through an `<a download>` click rather
 * than `window.location = <blob>` so the browser uses the suggested
 * filename instead of a hash. The blob URL is revoked on the next
 * tick to avoid leaks but late enough that the download actually
 * starts (Safari historically tears down blob URLs synchronously).
 */
export async function downloadVendorPriceListPdfAndSave(
  slug: string,
): Promise<string> {
  const { blob, filename } = await downloadVendorPriceListPdf(slug);
  saveBlobAsFile(blob, filename);
  return filename;
}

/**
 * Anon-keyed counterpart to `downloadVendorPriceListPdfAndSave` for
 * apps that boot without `initSupabase()` (specifically the public
 * storefront sub-app, `apps/shop/src/PublicStorefrontApp.tsx`) -- they
 * cannot call the session-aware `supabaseEndpoint()` helper so they
 * pass the URL + anon key explicitly.
 *
 * Behaviour is otherwise identical to the session-aware variant:
 * fetches the PDF bytes via `downloadVendorPriceListPdfViaFetch`,
 * pushes them through an `<a download>` click, and returns the
 * server-suggested filename so the caller can show a toast.
 */
export async function downloadVendorPriceListPdfAndSaveViaFetch(
  supabaseUrl: string,
  anonKey: string,
  slug: string,
): Promise<string> {
  const { blob, filename } = await downloadVendorPriceListPdfViaFetch(
    supabaseUrl,
    anonKey,
    slug,
  );
  saveBlobAsFile(blob, filename);
  return filename;
}
